import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import HttpBackend from 'i18next-http-backend'

/**
 * List of all translation namespaces fetched by the frontend, served at runtime by the engine's
 * `/api/v1/public/i18n/{{lng}}/{{ns}}` route (see engine `I18nConfig.NAMESPACES`). The engine list
 * additionally contains the backend-only `emails` namespace (email subjects/copy, ICU syntax) —
 * the frontend must NOT fetch it. Every other namespace must exist in both lists. Every namespace
 * is fetched eagerly since keys are flat and unique across files; `fallbackNS` below makes
 * `t('any_key')` resolve regardless of which namespace it actually lives in, so existing
 * `useTranslation('common')` call sites need no changes.
 *
 * To add a new feature area:
 *   1. Add its name here (and to the engine's `I18nConfig.NAMESPACES`)
 *   2. Create <namespace>.json under i18n/{en,it,ja}/
 *   3. Use it in components: useTranslation('common') still works via fallbackNS
 */
export const namespaces = [
  'common',
  'nav',
  'admin',
  'attendance',
  'checkin',
  'event-management',
  'events',
  'invitations',
  'org-members',
  'org-security',
  'organization',
  'participants',
  'registrations',
] as const

/** Union type of all valid namespace strings. */
export type Namespace = (typeof namespaces)[number]

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en', // missing keys resolve from English
    returnEmptyString: false, // Weblate exports untranslated entries as "" — treat as missing so they fall back to English instead of rendering blank
    supportedLngs: ['en', 'ja'],
    ns: namespaces,
    defaultNS: 'common',
    fallbackNS: namespaces.filter((n) => n !== 'common'),
    interpolation: {
      escapeValue: false,
    },
    backend: {
      loadPath: '/api/v1/public/i18n/{{lng}}/{{ns}}',
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
  })

export default i18n
